import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import InputMask from 'react-input-mask';

import {UserContext} from '../../UserContext';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';
import {usePreventDoubleClick} from '../hooks/usePreventDoubleClick';
import {useFormularioComReset} from '../hooks/useFormularioComReset';

import Formulario from '../../components/ui/Formulario';

import {formatarKwh10_2} from '../../util/validacoes';

import styles from './CadastroUsina.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function CadastroUsina() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {user} = useContext(UserContext);
    const {get, post, put} = useHttpClient();
    const {exibirErro} = useNotificacoes();
    const [cepValido, setCepValido] = useState(true);
    const [cepTouched, setCepTouched] = useState(false)
    const [tiposUsina, setTiposUsina] = useState([]);
    const [showSenha, setShowSenha] = useState(false);

    const [empresas, setEmpresas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [formData, setFormData, handleChange] = useFormularioComReset({
        idUsuarioProprietario: '',
        idEmpresa: user?.empresa?.id || '',
        cnpj: '',
        nome: '',
        email: '',
        emailContabilidade: '',
        idTipoUsina: '',
        telefone: '',
        endereco: '',
        codCliente: '',
        potencia: '',
        complementoEndereco: '',
        bairro: '',
        municipio: '',
        uf: '',
        cep: '',
        chaveAsaas: '',
        numeroCarteira: '',
        chavePix: '',
        tipoChavePix: '',
        cpfResponsavel: '',
        senhaDistribuidora: ''
    }, ['idEmpresa']);

    const fetchEmpresas = async () => {
        try {
            const data = await get(`/hello-sol/empresas/lista/${user?.empresa?.id}`);
            setEmpresas(Array.isArray(data) ? data : []);
        } catch {
            setEmpresas([]);
        }
    }
    const fetchTipoUsina = async () => {
        try {
            setTiposUsina(await get(`/hello-sol/tipos-usina`) || []);
        } catch {
            setTiposUsina([]);
        }
    }

    const fetchUsuarios = async (idEmpresa) => {
        try {
            const data = await get(`/hello-sol/usuarios/buscar?idEmpresa=${idEmpresa}&idTipoUsuario=1`);
            setUsuarios(Array.isArray(data) ? data : []);
        } catch {
            setUsuarios([]);
        }
    };


    const fetchUsina = async () => {
        try {
            const data = await get(`/hello-sol/usinas/${id}`);
            setFormData({
                ...data,
                cnpj: formatarCNPJ(data.cnpj),
                telefone: formatarTelefone(data.telefone),
                idEmpresa: data.empresaGestora?.id,
                idUsuarioProprietario: data.usuarioProprietario?.id,
                potencia: formatarKwh10_2(String(data.potencia))
            });
            setCepTouched(false);    // limpa o “tocado”
            setCepValido(true);      // e marca válido
        } catch {
            exibirErro('Erro ao carregar usina.');
        }
    };

    const fetchEndereco = async (cep) => {
        try {
            const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await res.json();
            if (data.erro) return exibirErro('CEP não encontrado');
            setFormData(prev => ({
                ...prev,
                endereco: data.logradouro,
                bairro: data.bairro,
                municipio: data.localidade,
                uf: data.uf
            }));
        } catch {
            exibirErro('Erro ao buscar endereço.');
        }
    };

    const formatarCNPJ = (valor) => valor.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");

    const formatarTelefone = (valor) => {
        if (valor.length === 10) return valor.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
        return valor.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
    };

    const limparChavePix = (valor, tipo) => {
        const apenasNumeros = valor.replace(/\D/g, '');
        switch (tipo) {
            case '1':
            case '2':
            case '3':
                return apenasNumeros;
            case '4':
            case '5':
            default:
                return valor.trim();
        }
    };

    const handleCepChange = (e) => {
        const cep = e.target.value;
        setCepTouched(true);
        setFormData(prev => ({...prev, cep}));
    };

    const handleTipoChavePixChange = (e) => {
        const tipo = e.target.value;
        setFormData(prev => ({...prev, tipoChavePix: tipo, chavePix: ''}));
    };

    const handleChavePixChange = (e) => {
        setFormData(prev => ({...prev, chavePix: e.target.value}));
    };

    const getPixMask = () => {
        switch (formData.tipoChavePix) {
            case '1':
                return '999.999.999-99';
            case '2':
                return '99.999.999/9999-99';
            case '3':
                return '(99) 99999-9999';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const cnpjOk = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(formData.cnpj);
        const telOk = /^\(\d{2}\) \d{4,5}-\d{4}$/.test(formData.telefone);
        const cepOk = /^\d{5}-\d{3}$/.test(formData.cep);
        if (!cnpjOk) return exibirErro('CNPJ inválido.');
        if (!telOk) return exibirErro('Telefone inválido.');
        if (!cepOk) return exibirErro('CEP inválido.');
        // Só valida CPF/senha quando NÃO for consórcio
        if (String(formData.idTipoUsina) !== '2') {
            const cpfOk = /^(?:\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11})$/.test(formData.cpfResponsavel || '');
            if (!cpfOk) return exibirErro('CPF do responsável inválido.');
            if (!formData.senhaDistribuidora?.trim()) return exibirErro('Informe a senha da distribuidora.');
        }
        return true
    };

    const salvarUsina = async () => {
        if (!validateForm()) return;

        let payload = {
            ...formData,
            chavePix: limparChavePix(formData.chavePix, formData.tipoChavePix)
        };

        // Se consórcio, não envie cpf/senha
        if (String(formData.idTipoUsina) === '2') {
            const {cpfResponsavel, senhaDistribuidora, ...rest} = payload;
            payload = rest;
        }

        try {
            if (id) await put(`/hello-sol/usinas/${id}`, payload);
            else await post(`/hello-sol/usinas`, payload);
            navigate('/home/listar-usina');
        } catch (error) {
            exibirErro(error.message || 'Erro ao salvar usina.');
        }
    };


    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarUsina);

    useEffect(() => {
        fetchEmpresas();
        fetchTipoUsina();
        if (id) fetchUsina();
    }, [id]);

    useEffect(() => {
        if (formData.idEmpresa) fetchUsuarios(formData.idEmpresa);
    }, [formData.idEmpresa]);

    useEffect(() => {
        if (String(formData.idTipoUsina) === '2') {
            setShowSenha(false); // opcional: volta o olho para “oculto”
            setFormData(prev => ({
                ...prev,
                cpfResponsavel: '',
                senhaDistribuidora: ''
            }));
        }
    }, [formData.idTipoUsina]);


    useEffect(() => {
        if (!cepTouched) {
            return;
        }

        // quando o CEP atinge 9 dígitos, tenta buscar
        if (!formData.cep || formData.cep.length < 9) {
            // volta ao estado inicial
            setCepValido(true);
            setFormData(prev => ({
                ...prev,
                endereco: '', bairro: '', municipio: '', uf: ''
            }));
            return;
        }

        fetch(`https://viacep.com.br/ws/${formData.cep.replace('-', '')}/json/`)
            .then(r => r.json())
            .then(d => {
                if (d.erro) {
                    exibirErro('CEP não encontrado. Preencha manualmente.');
                    setCepValido(false);  // libera os campos
                    setFormData(prev => ({
                        ...prev,
                        endereco: '', bairro: '', municipio: '', uf: ''
                    }));
                } else {
                    setCepValido(true);   // trava os campos novamente
                    setFormData(prev => ({
                        ...prev,
                        endereco: d.logradouro,
                        bairro: d.bairro,
                        municipio: d.localidade,
                        uf: d.uf
                    }));
                }
            })
            .catch(() => {
                exibirErro('Erro ao buscar endereço.');
                setCepValido(false);
            });
    }, [formData.cep]);

    return (
        <div className={styles_global.container}>
            <h2>{id ? 'Alterar Usina' : 'Cadastrar Usina'}</h2>

            <Formulario onSubmit={safeSubmit}>
                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idEmpresa">Empresa*</label>
                        <select
                            name="idEmpresa"
                            value={formData.idEmpresa}
                            onChange={handleChange}
                            id="idEmpresa"
                            required>
                            <option value="">Selecionar Empresa</option>
                            {empresas.map((empresa) => (
                                <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="cnpj">CNPJ*</label>
                        <InputMask
                            className="input-mask"
                            mask="99.999.999/9999-99"
                            maskChar=""
                            type="text"
                            name="cnpj"
                            value={formData.cnpj}
                            onChange={handleChange}
                            id="cnpj" required/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="nome">Nome*</label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            id="nome" required maxLength={100}/>
                    </div>
                </div>

                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="email">Email*</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            id="email" required/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="telefone">Telefone*</label>
                        <InputMask
                            className="input-mask"
                            mask="(99) 99999-9999"
                            maskChar=""
                            type="text"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            id="telefone" required/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="emailContabilidade">Email da Contabilidade</label>
                        <input
                            type="emailContabilidade"
                            name="emailContabilidade"
                            value={formData.emailContabilidade}
                            onChange={handleChange}
                            id="email"/>
                    </div>
                </div>
                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="codCliente">Cod. Cliente*</label>
                        <input
                            type="text"
                            name="codCliente"
                            value={formData.codCliente}
                            onChange={handleChange}
                            id="codCliente" required maxLength={15}/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsuarioProprietario">Proprietário*</label>
                        <select
                            name="idUsuarioProprietario"
                            value={formData.idUsuarioProprietario}
                            className={styles.campoProprietarioUsina}
                            onChange={handleChange}
                            id="idUsuarioProprietario" required>
                            <option value="">Selecionar Usuário</option>
                            {usuarios.map((usuario) => (
                                <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="potencia">Média de Geração Mensal kwh*</label>
                        <input
                            type="text"
                            name="potencia"
                            value={formData.potencia}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    potencia: formatarKwh10_2(e.target.value)
                                }))}
                            id="potencia" maxLength={11} required/>
                    </div>
                </div>
                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="cep">CEP*</label>
                        <InputMask
                            className="input-mask"
                            mask="99999-999"
                            maskChar=""
                            type="text"
                            name="cep"
                            value={formData.cep} onChange={handleCepChange}
                            id="cep" required/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="endereco">Endereço*</label>
                        <input
                            type="text"
                            name="endereco"
                            value={formData.endereco}
                            onChange={handleChange}
                            id="endereco"
                            required maxLength={100}/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="complementoEndereco">Complemento (opcional)</label>
                        <input
                            type="text"
                            name="complementoEndereco"
                            value={formData.complementoEndereco}
                            onChange={handleChange}
                            id="complementoEndereco" maxLength={50}/>
                    </div>
                </div>
                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="bairro">Bairro*</label>
                        <input
                            type="text"
                            name="bairro"
                            value={formData.bairro}
                            onChange={handleChange}
                            id="bairro"
                            required maxLength={50}/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="uf">UF*</label>
                        <input
                            type="text"
                            name="uf"
                            value={formData.uf}
                            onChange={handleChange}
                            id="uf"
                            required maxLength={2}/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="municipio">Município*</label>
                        <input
                            type="text"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleChange}
                            id="municipio"
                            required maxLength={50}/>
                    </div>
                </div>
                <div className={styles.linhaUsina3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idTipoUsina">Tipo de Usina*</label>
                        <select
                            name="idTipoUsina" value={formData.idTipoUsina}
                            onChange={handleChange}
                            id="idTipoUsina" required>
                            <option value="">Selecionar Tipo de Usina</option>
                            {tiposUsina.map((tiposUsina) => (<option
                                key={tiposUsina.id}
                                value={tiposUsina.id}>{tiposUsina.descricao}</option>))}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="tipoChavePix">Tipo de Chave Pix</label>
                        <select
                            name="tipoChavePix"
                            value={formData.tipoChavePix}
                            onChange={handleTipoChavePixChange}
                            id="tipoChavePix" required>
                            <option value="0">Selecione o tipo</option>
                            <option value="1">CPF</option>
                            <option value="2">CNPJ</option>
                            <option value="3">Telefone</option>
                            <option value="4">E-mail</option>
                            <option value="5">Chave Aleatória</option>
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="chavePix">Chave Pix</label>
                        <InputMask
                            mask={getPixMask()}
                            maskChar=""
                            type="text"
                            name="chavePix"
                            value={formData.chavePix}
                            onChange={handleChavePixChange}
                            id="chavePix"
                            placeholder={
                                formData.tipoChavePix === '1' ? 'Digite o CPF' :
                                    formData.tipoChavePix === '2' ? 'Digite o CNPJ' :
                                        formData.tipoChavePix === '3' ? 'Digite o telefone com DDD' :
                                            formData.tipoChavePix === '4' ? 'Digite o e-mail' :
                                                formData.tipoChavePix === '5' ? 'Digite a chave aleatória' :
                                                    'Selecione o tipo da Chave Pix'
                            }
                            required
                            disabled={!formData.tipoChavePix} // Desabilita o campo até que um tipo seja selecionado
                        />
                    </div>
                </div>

                {String(formData.idTipoUsina) !== '2' && formData.idTipoUsina && (
                    <div className={styles.linhaUsina3}>
                        <div className={styles_content.formField}>
                            <label htmlFor="senhaDistribuidora">Senha da Distribuidora*</label>
                            <div className={styles.passwordContainer}>
                                <input
                                    type={showSenha ? 'text' : 'password'}
                                    id="senhaDistribuidora"
                                    value={formData.senhaDistribuidora}
                                    onChange={(e) => setFormData(p => ({...p, senhaDistribuidora: e.target.value}))}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.eyeBtn}
                                    onClick={() => setShowSenha(s => !s)}
                                    aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                                    title={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showSenha ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24">
                                            <path d="M2 2l20 20M10.58 6.05A5 5 0 0117 12a5 5 0 01-6.95 4.58"
                                                  stroke="currentColor" strokeWidth="2" fill="none"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24">
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor"
                                                  strokeWidth="2" fill="none"/>
                                            <circle cx="12" cy="12" r="3" fill="currentColor"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={styles_content.formField}>
                            <label htmlFor="cpfResponsavel">CPF do Responsável*</label>
                            <div>
                                <InputMask
                                    mask="999.999.999-99"
                                    maskChar=""
                                    id="cpfResponsavel"
                                    name="cpfResponsavel"
                                    value={formData.cpfResponsavel}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.linhaUsina2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="chaveAsaas">Chave API Asaas</label>
                        <input
                            type="text"
                            name="chaveAsaas"
                            value={formData.chaveAsaas}
                            onChange={handleChange}
                            id="chaveAsaas"/>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="numeroCarteira">Número Carteira Asaas</label>
                        <input
                            type="text"
                            name="numeroCarteira"
                            value={formData.numeroCarteira}
                            onChange={handleChange}
                            id="numeroCarteira"/>
                    </div>
                </div>
                <div className={styles_content.buttonGroup}>
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroUsina;


